import { dateHelpers } from "@runn/calculations"

import type { ProjectBudgetForm_project$data } from "./__generated__/ProjectBudgetForm_project.graphql"
import type { ProjectBudgetForm_user$data } from "./__generated__/ProjectBudgetForm_user.graphql"

import {
  OTHER_EXPENSES_ROLE,
  OtherCostWithCalculations,
  addDerivedFields,
} from "~/helpers/otherCosts"
import { isBillableProject } from "~/helpers/project-helpers"

import { createEmptyExpense } from "./OtherExpenses/OtherExpensesEditor"
import { ProjectBudgetFormData } from "./formTypes"
import { getBudgetedRoles } from "./projectBudgetHelpers"
import { BudgetedRole, ProjectRate } from "./types"

const transformProjectRateFromGraphQL = (projectRate: any): ProjectRate => ({
  id: projectRate.id,
  projectId: projectRate.projectId, // Use aliased field name from GraphQL
  roleId: projectRate.roleId, // Use aliased field name from GraphQL
  rate: projectRate.rate,
})

const transformRoleForMutation = (role: BudgetedRole) => ({
  id: role.id,
  estimatedMinutes: role.estimatedMinutes,
  name: role.name,
  projectRoleId: role.projectRoleId,
  active: role.active,
})

const transformRateForMutation = (rate: ProjectRate, projectId: number) => ({
  id: rate.id,
  rate: rate.rate,
  roleId: rate.roleId,
  projectId: rate.projectId || projectId,
})

const transformExpenseForMutation = (expense: OtherCostWithCalculations) => {
  // Handle new expenses (string IDs starting with "new--")
  const id = String(expense.id).startsWith("new--")
    ? undefined
    : (expense.id as number)

  return {
    id,
    name: expense.name,
    cost: expense.cost,
    charge: expense.charge,
    date: dateHelpers.formatToRunnDate(expense.date),
    phaseId: expense.phaseId,
  }
}

// Transform GraphQL project data to form data
export const transformProjectToFormData = (
  project: ProjectBudgetForm_project$data,
  account: ProjectBudgetForm_user$data["account"],
): ProjectBudgetFormData => {
  const isBillable = isBillableProject(
    project.pricing_model,
    project.client.internal,
  )

  // Transform other costs to include calculated fields
  const projectOtherExpenses = (project.other_costs ?? []).map((expense) => {
    return {
      ...expense,
      date: dateHelpers.parseRunnDate(expense.date),
    }
  })

  const transformedExpenses = projectOtherExpenses.length
    ? projectOtherExpenses.map(addDerivedFields)
    : [createEmptyExpense()]

  return {
    // Project Configuration
    projectId: project.id,
    pricingModel: project.pricing_model as "fp" | "tm" | "nb",
    budgetMethod: project.budget_method as "ROLE" | "PHASE" | "PHASE_AND_ROLE",
    rateType: project.rate_type as "hours" | "days",
    rateCardId: project.rate_card.id,

    // Budget Fields
    totalBudget: project.total_budget,
    expensesBudget: project.expenses_budget,
    noPhaseBudget: project.no_phase_revenue_budget,

    // Role Management
    roles: getBudgetedRoles(project, account.roles, isBillable),
    deletedRoleIds: [],

    // Rate Management
    projectRates: project.project_rates.map(transformProjectRateFromGraphQL),
    deletedProjectRateIds: [],

    // Expense Management
    otherExpenses: transformedExpenses,

    // Phase Management
    phaseBudgets: project.phases.map((phase) => ({
      phaseId: phase.id,
      revenueBudget: phase.revenue_budget,
    })),
    phaseAndRoleBudgets: project.phases.flatMap((phase) =>
      (phase.phase_roles || []).map((phaseRole) => ({
        phaseId: phase.id,
        roleId: phaseRole.role.id,
        estimatedMinutes: phaseRole.estimated_minutes,
        projectId: project.id,
      })),
    ),
  }
}

// Transform form data to GraphQL mutation input
export const transformFormDataToMutation = (
  formData: ProjectBudgetFormData,
  hasFinancialPermissions: boolean,
) => {
  // Filter out "Other Expenses" role (id: -1) as it's handled separately via otherExpenses
  const filteredRoles = formData.roles.filter(
    (role) => role.id !== OTHER_EXPENSES_ROLE.id,
  )

  // Filter out deleted role IDs that are "Other Expenses"
  const filteredDeletedRoleIds = formData.deletedRoleIds.filter(
    (roleId) => roleId !== OTHER_EXPENSES_ROLE.id,
  )

  // Filter out project rates for "Other Expenses" role and ensure all rates have valid roleId
  const filteredProjectRates = formData.projectRates.filter(
    (rate) =>
      rate.roleId !== OTHER_EXPENSES_ROLE.id &&
      rate.roleId != null &&
      rate.roleId !== undefined,
  )

  const baseInput = {
    id: formData.projectId,
    roles: filteredRoles.map(transformRoleForMutation),
    unallocatedProjectRoleIds: filteredDeletedRoleIds,
  }

  // Add financial fields if user has permissions
  if (hasFinancialPermissions) {
    return {
      ...baseInput,
      pricingModel: formData.pricingModel,
      budgetMethod: formData.budgetMethod,
      totalBudget: formData.totalBudget,
      expensesBudget: formData.expensesBudget,
      rateType: formData.rateType,
      rateCardId: formData.rateCardId,
      rates: filteredProjectRates.map((rate) =>
        transformRateForMutation(rate, formData.projectId),
      ),
      unallocatedProjectRateIds: formData.deletedProjectRateIds,
      otherExpenses: formData.otherExpenses
        .filter((expense) => expense.name.trim())
        .map(transformExpenseForMutation),
      phaseBudgets:
        formData.budgetMethod === "PHASE" ? formData.phaseBudgets : undefined,
      noPhaseBudget:
        formData.budgetMethod === "PHASE" ? formData.noPhaseBudget : undefined,
      phaseRoleBudgets:
        formData.budgetMethod === "PHASE_AND_ROLE"
          ? formData.phaseAndRoleBudgets
          : undefined,
    }
  }

  return baseInput
}
